import { useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import RadioInput from 'LuxKit/Radio/RadioInput'
import TextArea from 'components/Common/Form/Input/TextArea'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import { formToObject } from 'lib/forms/formToObject'
import config from 'constants/config'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import { trackClientEvent } from 'analytics/analytics'

const RadioWrapper = styled.div`
  border: 1px solid ${(props) => props.theme.palette.neutral.default.five};

  > * + * {
    border-top: 1px solid ${(props) => props.theme.palette.neutral.default.five};
  }
`

const RefundReason = styled(VerticalSpacer)`
  padding: ${rem(20)};
`

const StyledRadioButtonInput = styled(RadioInput)`
  width: 100%;
`

interface Props {
  onNext: (reason: string, comment?: string) => void
  isReserveForZeroBooking?: boolean
  defaultReason?: string
  defaultComment?: string
  step: number
  maxSteps: number
}

// TODO - i18n: these strings are tricky because they are both displayed to the user, and sent to the backend as form value
const OTHER_OPTION: string = 'Other (please specify)'
const CHEAPER_OPTION: string = 'Found a cheaper deal'

export const REFUND_REASONS: Array<string> = [
  CHEAPER_OPTION,
  'Change in travel plans',
  'Personal reasons',
  'Financial constraints',
  'Travel restrictions',
  'Better loyalty program or rewards',
  OTHER_OPTION,
]

function HotelCancellationReason(props: Props) {
  const { onNext, step, maxSteps, isReserveForZeroBooking, defaultReason, defaultComment } = props
  const intl = useIntl()
  const [selectedReason, setSelectedReason] = useState<string | undefined>(defaultReason)

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const values = formToObject<{ reason: string, comment?: string }>(e.currentTarget)
    if (!values.reason || (values.reason && (values.reason === OTHER_OPTION || values.reason === CHEAPER_OPTION) && !values.comment)) {
      trackClientEvent({
        subject: 'cancellation_validation_error',
        action: 'missing_reason_or_comment',
        type: 'nonInteraction',
        category: 'refunds',
      })
    }
    onNext(values.reason, values.comment)
  }

  return (<form onSubmit={onSubmit}>
    <ModalHeader
      title={intl.formatMessage(
        {
          id: 'WFX7MpxCPGqILwDjI6kG',
          defaultMessage: 'Cancel {type}',
          description: 'Modal title for cancellation reason selection in a modal dialog',
        },
        { type: isReserveForZeroBooking ? intl.formatMessage({ id: 'rn3GI2EzUF6u8xko7FJ1', defaultMessage: 'reservation', description: 'Reservation type label for zero-upfront booking in a modal dialog' }) : intl.formatMessage({ id: 'mlvSMA_v8UzZXwFknJSJ', defaultMessage: 'booking', description: 'Type label identifying a booking item in the order summary or purchase history' }) },
      )}
      subtitle={intl.formatMessage(
        {
          id: 'HuGSdAqp2_pVMNB4hO70',
          defaultMessage: 'Step {current} of {total}',
          description: 'Step progress indicator in hotel cancellation reason flow',
        },
        { current: step, total: maxSteps },
      )}
    />
    <ModalBody>
      <ModalContent>
        <Group direction="vertical" gap={24}>
          <BodyText variant="large">
            {intl.formatMessage(
              {
                id: 'e9Ulrc9Z9r74hyFxbot0',
                defaultMessage: 'Before canceling your booking, please select a reason; your feedback will help us enhance the {brand} experience.',
                description: 'Body text asking user to select a cancellation reason',
              },
              { brand: config.title },
            )}
          </BodyText>
          <Heading variant="heading6">
            {intl.formatMessage({
              id: 'aNzYB5vg5cVJWHRjeMbp',
              defaultMessage: 'Select your reason for cancelling',
              description: 'Heading for cancellation reason selection in the cancellation flow',
            })}
          </Heading>
          <RadioWrapper>
            {REFUND_REASONS.map((option) => (
              <RefundReason data-testid="refund-reason" key={option} gap={8}>
                <StyledRadioButtonInput
                  checked={selectedReason === option}
                  value={option}
                  name="reason"
                  required
                  onChange={() => setSelectedReason(option)}
                >
                  {option}
                </StyledRadioButtonInput>
                {selectedReason === OTHER_OPTION && option === OTHER_OPTION && (
                  <TextArea
                    placeholder={intl.formatMessage({
                      id: 'cu6OQVVhOzD1hR0JD9mO',
                      defaultMessage: 'Specify reason',
                      description: 'Placeholder for textarea to specify other cancellation reason',
                    })}
                    name="comment"
                    data-testid="other-reason"
                    defaultValue={defaultReason === OTHER_OPTION ? defaultComment : undefined}
                    required
                    noValidationSpacing
                  />
                )}
                {selectedReason === CHEAPER_OPTION && option === CHEAPER_OPTION && (
                  <TextArea
                    placeholder={intl.formatMessage({
                      id: 'f_dBpD-0f1FzZtAW6BdU',
                      defaultMessage: 'Who had the cheaper deal?',
                      description: 'Placeholder for textarea to specify who had a cheaper deal for hotel cancellation',
                    })}
                    name="comment"
                    defaultValue={defaultReason === CHEAPER_OPTION ? defaultComment : undefined}
                    required
                    noValidationSpacing
                  />
                )}
              </RefundReason>
            ))}
          </RadioWrapper>
        </Group>
      </ModalContent>
    </ModalBody>
    <ModalFooter
      primaryActionProps={{
        'data-testid': 'cancel-booking-confirm-button',
        type: 'submit',
        children: intl.formatMessage({
          id: '07zWkGiDL3aJKQnotjsI',
          defaultMessage: 'Continue',
          description: 'Button label to continue to next step in cancellation reason flow',
        }),
      }}
    />
  </form>)
}

export default HotelCancellationReason
